/**
 * 맵 뷰어 런처 — ROI 편집기 / 실시간 관제 같은 페이지에서 "이 현장에 등록된 맵"
 * 을 뷰어로 빠르게 띄울 수 있게 한다.
 *
 * 동작:
 *   - 마운트 시 /v0.1/map-editor/projects?site_project_id=<id> 로 맵 목록 fetch.
 *   - 1건이면 단일 버튼 — 클릭 시 새 탭으로 /project/<siteId>/map-viewer/<mapId>.
 *   - 2건 이상이면 드롭다운 — 선택해서 새 탭으로 열기.
 *   - 없으면 disabled 상태로 "맵 없음" 표기.
 *
 * 새 탭 정책: 현재 워크플로(ROI 편집 / 실시간 관제) 를 잃지 않고 맵을 사이드
 * 화면에 띄울 수 있도록 새 탭. inline embed 대신 가장 단순한 접근.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { Map as MapIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { API_ENDPOINTS } from '../config/api';
import { apiClient } from '../services/api/apiClient';
import { MapProject } from '../map-editor-shared/api/types/mapEditor';

interface Props {
  siteProjectId: number;
  /** 버튼 size — 'small' 권장 (헤더/패널에 박힐 때) */
  size?: 'small' | 'medium';
  /** 변형: contained(강조) | outlined(약함) */
  variant?: 'contained' | 'outlined' | 'text';
}

const MapViewerLauncher: React.FC<Props> = ({
  siteProjectId,
  size = 'small',
  variant = 'outlined',
}) => {
  const [maps, setMaps] = useState<MapProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient
      .get(API_ENDPOINTS.MAP_EDITOR.LIST_PROJECTS(siteProjectId))
      .then((resp) => {
        if (cancelled) return;
        // 응답 envelope 모양이 site 마다 달라질 수 있으므로 data 위치를 유연 처리.
        const raw = (resp.data?.data ?? resp.data ?? []) as MapProject[];
        setMaps(Array.isArray(raw) ? raw.filter((m) => !m.deleted_at) : []);
      })
      .catch(() => {
        if (!cancelled) setMaps([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [siteProjectId]);

  const openViewer = (mapId: number) => {
    const url = `/project/${siteProjectId}/map-viewer/${mapId}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const single = useMemo(() => (maps.length === 1 ? maps[0] : null), [maps]);

  if (loading) {
    return (
      <Button size={size} variant={variant} disabled startIcon={<CircularProgress size={14} />}>
        맵 불러오는 중
      </Button>
    );
  }

  if (maps.length === 0) {
    return (
      <Tooltip title="이 현장에 등록된 맵이 없습니다.">
        <span>
          <Button size={size} variant={variant} disabled startIcon={<MapIcon />}>
            맵 뷰어
          </Button>
        </span>
      </Tooltip>
    );
  }

  if (single) {
    return (
      <Button
        size={size}
        variant={variant}
        color="primary"
        startIcon={<MapIcon />}
        endIcon={<OpenInNewIcon fontSize="small" />}
        onClick={() => openViewer(single.id)}
        sx={{ textTransform: 'none' }}
      >
        맵 뷰어 ({single.name})
      </Button>
    );
  }

  return (
    <>
      <Button
        size={size}
        variant={variant}
        color="primary"
        startIcon={<MapIcon />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{ textTransform: 'none' }}
      >
        맵 뷰어 ({maps.length}개)
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {maps.map((m) => (
          <MenuItem
            key={m.id}
            onClick={() => {
              setAnchorEl(null);
              openViewer(m.id);
            }}
          >
            <OpenInNewIcon fontSize="small" sx={{ mr: 1 }} />
            {m.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default MapViewerLauncher;
